// components/use-case-details/use-case-workflow/use-case-visualization-component.tsx
// components/use-case-details/workflow-story-player.tsx
"use client";

import { AnimatePresence,motion } from 'framer-motion';
import { Bot, Brain, ChevronLeft, ChevronRight, Database, 
         FileSearch, FileSpreadsheet, Pause, Play, RotateCcw, Server,Users } from 'lucide-react';
import React, { useEffect,useState } from 'react';

// Types
interface WorkflowScene {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  animationComponent: React.FC<{isActive: boolean; onComplete?: () => void}>;
  nextSceneId?: string;
}

interface WorkflowStoryProps {
  title: string;
  description?: string;
  scenes: WorkflowScene[];
  autoPlay?: boolean;
  loop?: boolean;
}

// Animation components for each scene
// 1. Fixed DataScrapingAnimation
export const DataScrapingAnimation: React.FC<{isActive: boolean; onComplete?: () => void}> = ({ isActive, onComplete }) => {
  useEffect(() => {
    if (isActive) {
      const timer = setTimeout(() => {
        onComplete && onComplete();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isActive, onComplete]);
  
  return (
    <div className="h-full w-full flex items-center justify-center">
      <div className="relative h-72 w-full max-w-2xl">
        {/* Larger, labeled building/websites representations */}
        <motion.div 
          className="absolute top-0 left-0 h-32 w-28 bg-blue-500/20 rounded-lg border border-blue-500/40 flex flex-col items-center justify-center"
          animate={isActive ? { 
            y: [0, -10, 0],
            opacity: [0.7, 1, 0.7] 
          } : {}}
          transition={{ 
            duration: 2,
            repeat: isActive ? Infinity : 0,
            repeatType: "reverse"
          }}
        >
          <Database size={24} className="text-blue-400 mb-2" />
          <span className="text-white text-xs font-medium text-center">City Planning Office</span>
        </motion.div>
        
        <motion.div 
          className="absolute top-10 left-40 h-28 w-32 bg-purple-500/20 rounded-lg border border-purple-500/40 flex flex-col items-center justify-center"
          animate={isActive ? { 
            y: [0, -5, 0],
            opacity: [0.7, 1, 0.7] 
          } : {}}
          transition={{ 
            duration: 1.5,
            delay: 0.2,
            repeat: isActive ? Infinity : 0,
            repeatType: "reverse"
          }}
        >
          <Database size={24} className="text-purple-400 mb-2" />
          <span className="text-white text-xs font-medium text-center">Zoning Registry</span>
        </motion.div>
        
        <motion.div 
          className="absolute top-5 right-10 h-30 w-32 bg-green-500/20 rounded-lg border border-green-500/40 flex flex-col items-center justify-center"
          animate={isActive ? { 
            y: [0, -8, 0],
            opacity: [0.7, 1, 0.7] 
          } : {}}
          transition={{ 
            duration: 1.8,
            delay: 0.4,
            repeat: isActive ? Infinity : 0,
            repeatType: "reverse"
          }}
        >
          <Database size={24} className="text-green-400 mb-2" />
          <span className="text-white text-xs font-medium text-center">Property Records</span>
        </motion.div>
        
        {/* Larger AI scraping bot */}
        <motion.div 
          className="absolute top-40 left-1/2 transform -translate-x-1/2 flex flex-col items-center justify-center"
          animate={isActive ? {
            y: [-40, -40, -90, -90, -50, -50, -70, -70, -40],
            x: [-120, -120, -40, -40, 50, 50, 120, 120, -120],
            rotate: [0, 0, 90, 90, 180, 180, 270, 270, 360]
          } : {}}
          transition={{
            duration: 5,
            repeat: isActive ? Infinity : 0,
            times: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1]
          }}
        >
          <div className="bg-blue-600 rounded-full p-3">
            <Bot className="text-white" size={24} />
          </div>
          
          {/* Data particles flowing from buildings to bot */}
          {isActive && (
            <>
              <motion.div
                className="absolute h-3 w-3 rounded-full bg-blue-400"
                initial={{ x: -100, y: -120, opacity: 0 }}
                animate={{ x: 0, y: 0, opacity: [0, 1, 0] }}
                transition={{ duration: 0.8, repeat: Infinity, repeatDelay: 1 }}
              />
              <motion.div
                className="absolute h-3 w-3 rounded-full bg-purple-400"
                initial={{ x: -60, y: -100, opacity: 0 }}
                animate={{ x: 0, y: 0, opacity: [0, 1, 0] }}
                transition={{ duration: 0.8, delay: 0.3, repeat: Infinity, repeatDelay: 1 }}
              />
              <motion.div
                className="absolute h-3 w-3 rounded-full bg-green-400"
                initial={{ x: 80, y: -110, opacity: 0 }}
                animate={{ x: 0, y: 0, opacity: [0, 1, 0] }}
                transition={{ duration: 0.8, delay: 0.6, repeat: Infinity, repeatDelay: 1 }}
              />
            </>
          )}
        </motion.div>
        
        {/* Larger database target */}
        <motion.div 
          className="absolute bottom-0 left-1/2 transform -translate-x-1/2 bg-gray-800 rounded-lg p-4 border border-blue-500/30 flex flex-col items-center"
          animate={isActive ? { 
            y: [0, -5, 0],
            boxShadow: [
              '0 0 0 rgba(59, 130, 246, 0)',
              '0 0 20px rgba(59, 130, 246, 0.5)',
              '0 0 0 rgba(59, 130, 246, 0)'
            ]
          } : {}}
          transition={{ 
            duration: 2,
            repeat: isActive ? Infinity : 0,
            repeatType: "reverse"
          }}
        >
          <Database className="text-blue-500 mb-1" size={32} />
          <span className="text-white text-sm font-medium">Land Data Brain</span>
        </motion.div>
        
        {/* Data flow to database */}
        {isActive && (
          <motion.div
            className="absolute bottom-24 left-1/2 transform -translate-x-1/2 h-32 w-2 bg-gradient-to-b from-blue-500/0 to-blue-500"
            initial={{ scaleY: 0, opacity: 0 }}
            animate={{ scaleY: 1, opacity: [0, 1, 0.5] }}
            transition={{ duration: 1, repeat: Infinity }}
          />
        )}
      </div>
    </div>
  );
};

// Scene 2
export const QueryDatabaseAnimation: React.FC<{isActive: boolean; onComplete?: () => void}> = ({ isActive, onComplete }) => {
  useEffect(() => {
    if (isActive) {
      const timer = setTimeout(() => {
        onComplete && onComplete();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isActive, onComplete]);
  
  return (
    <div className="h-full w-full flex items-center justify-center">
      <div className="relative h-72 w-full max-w-2xl">
        {/* User */}
        <motion.div 
          className="absolute top-10 left-0 flex items-center"
          animate={isActive ? { x: [0, 20, 0] } : {}}
          transition={{ duration: 3, repeat: isActive ? Infinity : 0 }}
        >
          <motion.div 
            className="bg-purple-600 rounded-full p-3"
            animate={isActive ? { 
              scale: [1, 1.1, 1],
              boxShadow: [
                '0 0 0 rgba(147, 51, 234, 0)',
                '0 0 10px rgba(147, 51, 234, 0.5)',
                '0 0 0 rgba(147, 51, 234, 0)'
              ]
            } : {}}
            transition={{ duration: 1.5, repeat: isActive ? Infinity : 0 }}
          >
            <Users className="text-white" size={24} />
          </motion.div>
          <div className="ml-2 bg-gray-800 p-3 rounded-lg">
            <span className="text-white text-sm">Need properties in Downtown East...</span>
          </div>
        </motion.div>
        
        {/* Database in center */}
        <motion.div 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gray-800 p-5 rounded-lg border border-blue-500/30 flex flex-col items-center"
          animate={isActive ? { 
            scale: [1, 1.05, 1],
            boxShadow: [
              '0 0 0 rgba(59, 130, 246, 0)',
              '0 0 20px rgba(59, 130, 246, 0.3)',
              '0 0 0 rgba(59, 130, 246, 0)'
            ]
          } : {}}
          transition={{ duration: 3, repeat: isActive ? Infinity : 0 }}
        >
          <Database className="text-blue-500 mb-1" size={36} />
          <span className="text-white text-sm font-medium">Land Data Brain</span>
        </motion.div>
        
        {/* Semantic search transformation - NEW */}
        {isActive && (
          <motion.div 
            className="absolute top-20 left-1/3 bg-gray-800 px-3 py-2 rounded-lg border border-purple-500/30"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: [0, 1, 1, 0], scale: [0.8, 1, 1, 0.8], y: [0, -10, -10, 0] }}
            transition={{ duration: 2, times: [0, 0.3, 0.7, 1], delay: 0.5, repeat: Infinity, repeatDelay: 2 }}
          >
            <div className="text-xs text-purple-300 mb-1">Semantic Transformation</div>
            <code className="text-xs text-white">location:"Downtown East" AND type:"residential" AND roi:&gt;15%</code>
          </motion.div>
        )}
        
        {/* Query going from user to database */}
        {isActive && (
          <>
            <motion.div 
              className="absolute top-20 left-32 h-3 w-3 rounded-full bg-purple-500"
              animate={{
                x: [0, 80, 160],
                y: [0, 30, 60],
                opacity: [0, 1, 0]
              }}
              transition={{ duration: 1, repeat: Infinity, repeatDelay: 1 }}
            />
            
            <motion.div 
              className="absolute top-20 left-32 h-3 w-3 rounded-full bg-purple-500"
              animate={{
                x: [0, 80, 160],
                y: [0, 30, 60],
                opacity: [0, 1, 0]
              }}
              transition={{ duration: 1, delay: 0.3, repeat: Infinity, repeatDelay: 1 }}
            />
          </>
        )}
        
        {/* Database search animation - NEW */}
        {isActive && (
          <motion.div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-52 h-52 rounded-full border-2 border-blue-500/30"
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.1, 0.3, 0.1],
              rotate: [0, 180, 360]
            }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          />
        )}
        
        {/* Results flowing back from database - moved right */}
        {isActive && (
          <div className="absolute bottom-10 right-0">
            <motion.div 
              className="bg-gray-800 p-3 rounded-lg border-l-4 border-green-500"
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 2.5, duration: 0.5 }}
            >
              <div className="text-white text-sm font-medium">Property A: 123 Main St</div>
              <div className="text-gray-400 text-xs">ROI: 17.2%</div>
            </motion.div>
            
            <motion.div 
              className="bg-gray-800 p-3 rounded-lg border-l-4 border-green-500 mt-2"
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 3, duration: 0.5 }}
            >
              <div className="text-white text-sm font-medium">Property B: 456 Market St</div>
              <div className="text-gray-400 text-xs">ROI: 16.5%</div>
            </motion.div>
            
            <motion.div 
              className="bg-gray-800 p-3 rounded-lg border-l-4 border-yellow-500 mt-2"
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 3.5, duration: 0.5 }}
            >
              <div className="text-white text-sm font-medium">Property C: 789 Central Ave</div>
              <div className="text-gray-400 text-xs">ROI: 14.8%</div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};


// Scene 3
// 3. Fixed OptimizationAnimation
export const OptimizationAnimation: React.FC<{isActive: boolean; onComplete?: () => void}> = ({ isActive, onComplete }) => {
  useEffect(() => {
    if (isActive) {
      const timer = setTimeout(() => {
        onComplete && onComplete();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isActive, onComplete]);
  
  return (
    <div className="h-full w-full flex items-center justify-center">
      <div className="relative h-72 w-full max-w-2xl">
        {/* Selected property */}
        <motion.div 
          className="absolute top-10 left-0 bg-gray-800 p-3 rounded-lg border-l-4 border-green-500"
          animate={isActive ? { 
            x: [0, 10, 0],
            boxShadow: [
              '0 0 0 rgba(34, 197, 94, 0)',
              '0 0 10px rgba(34, 197, 94, 0.3)',
              '0 0 0 rgba(34, 197, 94, 0)'
            ]
          } : {}}
          transition={{ duration: 2, repeat: isActive ? Infinity : 0 }}
        >
          <div className="text-white text-sm font-medium">Property A: 123 Main St</div>
          <div className="text-gray-400 text-xs">ROI: 17.2%</div>
        </motion.div>
        
        {/* AI optimization in center */}
        <motion.div 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gray-800 p-5 rounded-lg border border-blue-500/30 flex flex-col items-center z-10"
          animate={isActive ? { 
            rotate: [0, 3, 0, -3, 0],
            boxShadow: [
              '0 0 0 rgba(59, 130, 246, 0)',
              '0 0 25px rgba(59, 130, 246, 0.4)',
              '0 0 0 rgba(59, 130, 246, 0)'
            ]
          } : {}}
          transition={{ duration: 3, repeat: isActive ? Infinity : 0 }}
        >
          <Brain className="text-blue-500 mb-1" size={36} />
          <span className="text-white text-sm font-medium">Design Optimizer</span>
        </motion.div>
        
        {/* Better processing visualization - gears and calculations */}
        {isActive && (
          <>
            {/* Gear animation ring */}
            <motion.div 
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64"
              style={{ zIndex: 5 }}
            >
              {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
                <motion.div
                  key={i}
                  className="absolute w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center"
                  style={{
                    top: `${50 + 40 * Math.sin(angle * Math.PI / 180)}%`,
                    left: `${50 + 40 * Math.cos(angle * Math.PI / 180)}%`,
                  }}
                  animate={{ 
                    rotate: [0, 360],
                    scale: [1, i % 2 === 0 ? 1.2 : 0.8, 1]
                  }}
                  transition={{ 
                    rotate: { duration: 10, repeat: Infinity, ease: "linear" },
                    scale: { duration: 2, repeat: Infinity, repeatType: "reverse", delay: i * 0.2 }
                  }}
                >
                  <div className="w-4 h-4 bg-blue-500/30 rounded-sm rotate-45" />
                </motion.div>
              ))}
            </motion.div>
            
            {/* Calculation particles */}
            {[...Array(4)].map((_, i) => (
              <motion.div
                key={`calc-${i}`}
                className="absolute top-1/2 left-1/2 px-2 py-1 bg-gray-900 rounded text-xs text-blue-300"
                initial={{ 
                  x: 0, y: 0, 
                  opacity: 0,
                  rotate: Math.random() * 20 - 10
                }}
                animate={{ 
                  x: Math.random() * 140 - 70,
                  y: Math.random() * 140 - 70,
                  opacity: [0, 1, 0]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  delay: i * 0.5,
                  repeatDelay: Math.random() * 2
                }}
              >
                {['ROI = 16.3%', 'H/W = 3.5', 'FAR = 4.2', '120 units'][i]}
              </motion.div>
            ))}
          </>
        )}
        
        {/* Design variations output */}
        {isActive && (
          <div className="absolute bottom-5 right-0 space-y-2">
            <motion.div 
              className="bg-gray-800 p-3 rounded-lg border-l-4 border-blue-500"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 2 }}
            >
              <div className="text-white text-sm font-medium">Design A: Tower + Podium</div>
              <div className="text-gray-400 text-xs">120 units, $18.5M cost, 16.3% ROI</div>
            </motion.div>
            
            <motion.div 
              className="bg-gray-800 p-3 rounded-lg border-l-4 border-blue-500"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 2.5 }}
            >
              <div className="text-white text-sm font-medium">Design B: Courtyard Layout</div>
              <div className="text-gray-400 text-xs">104 units, $16.8M cost, 15.7% ROI</div>
            </motion.div>
            
            <motion.div 
              className="bg-gray-800 p-3 rounded-lg border-l-4 border-blue-500"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 3 }}
            >
              <div className="text-white text-sm font-medium">Design C: Split Towers</div>
              <div className="text-gray-400 text-xs">132 units, $19.7M cost, 14.2% ROI</div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

// Error detection animation component
// 4. Fixed ErrorDetectionAnimation
export const ErrorDetectionAnimation: React.FC<{isActive: boolean; onComplete?: () => void}> = ({ isActive, onComplete }) => {
  useEffect(() => {
    if (isActive) {
      const timer = setTimeout(() => {
        onComplete && onComplete();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isActive, onComplete]);
  
  return (
    <div className="h-full w-full flex items-center justify-center">
      <div className="relative h-72 w-full max-w-2xl">
        {/* Multiple financial documents, better positioned */}
        <div className="absolute top-10 left-10">
          <motion.div 
            className="absolute top-0 left-0 bg-gray-800 p-4 rounded-lg border border-gray-600 transform rotate-[-5deg] z-10"
            animate={isActive ? { 
              y: [0, -5, 0],
              rotate: [-5, -3, -5],
            } : {}}
            transition={{ duration: 3, repeat: isActive ? Infinity : 0 }}
          >
            <FileSpreadsheet className="text-blue-400" size={28} />
            <div className="text-white text-xs mt-1">Q2_Financial.xlsx</div>
          </motion.div>
          
          <motion.div 
            className="absolute top-8 left-6 bg-gray-800 p-4 rounded-lg border border-gray-600 transform rotate-3 z-20"
            animate={isActive ? { 
              y: [0, -3, 0],
              rotate: [3, 5, 3],
            } : {}}
            transition={{ duration: 2.5, delay: 0.3, repeat: isActive ? Infinity : 0 }}
          >
            <FileSpreadsheet className="text-green-400" size={28} />
            <div className="text-white text-xs mt-1">Budget_2025.xlsx</div>
          </motion.div>
          
          <motion.div 
            className="absolute top-4 left-12 bg-gray-800 p-4 rounded-lg border border-gray-600 transform rotate-0 z-30"
            animate={isActive ? { 
              y: [0, -2, 0],
              rotate: [0, 2, 0],
            } : {}}
            transition={{ duration: 2.8, delay: 0.5, repeat: isActive ? Infinity : 0 }}
          >
            <FileSpreadsheet className="text-red-400" size={28} />
            <div className="text-white text-xs mt-1">Projections.xlsx</div>
          </motion.div>
        </div>
        
        {/* Error detection agent with magnifying glass */}
        <motion.div 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
          animate={isActive ? { 
            x: [0, -40, 40, 0],
            y: [0, -20, 20, 0],
          } : {}}
          transition={{ duration: 5, repeat: isActive ? Infinity : 0 }}
        >
          <motion.div 
            className="relative z-10"
            animate={isActive ? { 
              scale: [1, 1.1, 1],
              rotate: [0, 5, 0, -5, 0]
            } : {}}
            transition={{ duration: 3, repeat: isActive ? Infinity : 0 }}
          >
            {/* Magnifying glass with error detection icon */}
            <div className="relative">
              <div className="w-24 h-24 rounded-full border-4 border-red-500 bg-transparent relative overflow-hidden">
                <div className="absolute inset-0 bg-gray-900/50" />
                <motion.div 
                  className="absolute inset-0 border-t-2 border-red-500/50"
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                />
              </div>
              <div className="absolute right-0 bottom-0 w-8 h-20 rounded-full bg-red-500 transform rotate-45 translate-x-6 translate-y-6" />
              
              <motion.div 
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full p-2"
                animate={isActive ? { 
                  scale: [1, 1.2, 1] 
                } : {}}
                transition={{ duration: 1, repeat: isActive ? Infinity : 0 }}
              >
                <FileSearch className="text-white" size={24} />
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
        
        {/* Errors detected */}
        {isActive && (
          <div className="absolute bottom-5 right-0 space-y-2">
            <motion.div 
              className="bg-gray-800 p-3 rounded-lg border-l-4 border-red-500"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 2 }}
            >
              <div className="text-white text-sm font-medium">ROI Calculation Error</div>
              <div className="text-gray-400 text-xs">Incorrect construction cost value</div>
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-400">Confidence: 97%</span>
                <span className="text-xs text-red-400">High Impact</span>
              </div>
            </motion.div>
            
            <motion.div 
              className="bg-gray-800 p-3 rounded-lg border-l-4 border-yellow-500"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 2.8 }}
            >
              <div className="text-white text-sm font-medium">Unusual Revenue Projection</div>
              <div className="text-gray-400 text-xs">Values deviate 35% from historical average</div>
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-400">Confidence: 82%</span>
                <span className="text-xs text-yellow-400">Medium Impact</span>
              </div>
            </motion.div>
          </div>
        )}
        
        {/* Error particles - more distinctive */}
        {isActive && (
          <>
            <motion.div 
              className="absolute top-20 left-40 h-4 w-4 rounded-full bg-red-500"
              animate={{
                x: [0, 60, 120],
                y: [0, 40, 80],
                opacity: [0, 1, 0],
                scale: [1, 1.5, 1]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            
            <motion.div 
              className="absolute top-40 left-60 h-4 w-4 rounded-full bg-yellow-500"
              animate={{
                x: [0, 40, 80],
                y: [0, 20, 40],
                opacity: [0, 1, 0],
                scale: [1, 1.5, 1]
              }}
              transition={{ duration: 2, delay: 1, repeat: Infinity }}
            />
            
            <motion.div 
              className="absolute top-30 left-80 h-4 w-4 rounded-full bg-red-500"
              animate={{
                x: [0, 50, 100],
                y: [0, 30, 60],
                opacity: [0, 1, 0],
                scale: [1, 1.5, 1]
              }}
              transition={{ duration: 2, delay: 0.5, repeat: Infinity }}
            />
          </>
        )}
      </div>
    </div>
  );
};

// Human decision making animation
// 5. Fixed HumanDecisionAnimation
export const HumanDecisionAnimation: React.FC<{isActive: boolean; onComplete?: () => void}> = ({ isActive, onComplete }) => {
  useEffect(() => {
    if (isActive) {
      const timer = setTimeout(() => {
        onComplete && onComplete();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isActive, onComplete]);
  
  return (
    <div className="h-full w-full flex items-center justify-center">
      <div className="relative h-72 w-full max-w-2xl">
        {/* AI recommender on left */}
        <motion.div 
          className="absolute top-1/2 left-4 transform -translate-y-1/2 bg-gray-800 p-4 rounded-lg border border-blue-500/30 flex flex-col items-center"
          animate={isActive ? { 
            x: [0, 10, 0],
            boxShadow: [
              '0 0 0 rgba(59, 130, 246, 0)',
              '0 0 15px rgba(59, 130, 246, 0.3)',
              '0 0 0 rgba(59, 130, 246, 0)'
            ]
          } : {}}
          transition={{ duration: 3, repeat: isActive ? Infinity : 0 }}
        >
          <Bot className="text-blue-500 mb-1" size={28} />
          <span className="text-white text-xs">AI Agent</span>
        </motion.div>
        
        {/* Human in center */}
        <motion.div 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gray-800 p-5 rounded-lg border border-purple-500/30 flex flex-col items-center"
          animate={isActive ? { 
            scale: [1, 1.05, 1],
            boxShadow: [
              '0 0 0 rgba(147, 51, 234, 0)',
              '0 0 20px rgba(147, 51, 234, 0.3)',
              '0 0 0 rgba(147, 51, 234, 0)'
            ]
          } : {}}
          transition={{ duration: 2, repeat: isActive ? Infinity : 0 }}
        >
          <Users className="text-purple-500 mb-1" size={36} />
          <span className="text-white text-sm">Decision Maker</span>
        </motion.div>
        
        {/* Data flow from AI to human - clear, non-overlapping */}
        {isActive && (
          <motion.div 
            className="absolute top-1/2 left-24 h-2 w-64 origin-left"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1 }}
          >
            <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500" />
            
            {/* Data particles */}
            <motion.div 
              className="absolute top-0 left-0 h-4 w-4 rounded-full bg-blue-500 -translate-y-1/2"
              animate={{ x: [0, 64, 128, 192, 256] }}
              transition={{ duration: 2, repeat: Infinity, repeatType: "loop" }}
            />
          </motion.div>
        )}
        
        {/* Decision options - well spaced on right */}
        {isActive && (
          <div className="absolute top-5 right-5 space-y-3">
            <motion.div 
              className="bg-gray-800 p-3 rounded-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              <div className="text-white text-sm">Design A: Tower + Podium</div>
            </motion.div>
            
            <motion.div 
              className="bg-gray-800 p-3 rounded-lg border-2 border-green-500"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5 }}
            >
              <div className="text-white text-sm">Design B: Courtyard Layout</div>
              <motion.div 
                className="mt-1 bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded inline-flex items-center"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 3 }}
              >
                <Users size={12} className="mr-1" />
                <span>Selected</span>
              </motion.div>
            </motion.div>
            
            <motion.div 
              className="bg-gray-800 p-3 rounded-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2 }}
            >
              <div className="text-white text-sm">Design C: Split Towers</div>
            </motion.div>
          </div>
        )}
        
        {/* Feedback flow back to system - clear path, non-overlapping */}
        {isActive && (
          <>
            <motion.div 
              className="absolute bottom-10 left-1/2 h-16 w-2 bg-gradient-to-b from-purple-500 to-blue-500 origin-top"
              initial={{ scaleY: 0, opacity: 0 }}
              animate={{ scaleY: 1, opacity: [0, 1] }}
              transition={{ delay: 3.5, duration: 1 }}
            />
            
            <motion.div 
              className="absolute bottom-0 left-1/2 transform -translate-x-1/2 bg-gray-800 p-3 rounded-lg border border-blue-500/30 flex flex-col items-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 4 }}
            >
              <Server className="text-blue-500 mb-1" size={24} />
              <div className="text-xs text-white">Learning from decision</div>
            </motion.div>
            
            {/* Show learning process */}
            <motion.div
              className="absolute bottom-20 left-1/2 transform -translate-x-1/2 px-3 py-1 bg-blue-500/20 rounded-full"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ 
                opacity: [0, 1, 0],
                scale: [0.8, 1, 0.8],
                y: [0, -20, -40]
              }}
              transition={{ delay: 4.5, duration: 1 }}
            >
              <span className="text-xs text-blue-300">Updating model weights...</span>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
};

// Core story player component
export const WorkflowStoryPlayer: React.FC<WorkflowStoryProps> = ({
  title,
  description,
  scenes,
  autoPlay = true,
  loop = true
}) => {
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [playedOnce, setPlayedOnce] = useState(false);
  
  // Map scene IDs to indices for navigation
  const sceneIndexMap = scenes.reduce((map, scene, index) => {
    map[scene.id] = index;
    return map;
  }, {} as Record<string, number>);
  
  const currentScene = scenes[currentSceneIndex];
  
  // Auto-advance through scenes
  useEffect(() => {
    if (!isPlaying) return;
    
    // Mark that we've played once
    if (!playedOnce) {
      setPlayedOnce(true);
    }
  }, [isPlaying, playedOnce]);
  
  // Handle scene completion
  const handleSceneComplete = () => {
    if (!isPlaying) return;
    
    if (currentScene.nextSceneId) {
      // Navigate to the specified next scene
      const nextIndex = sceneIndexMap[currentScene.nextSceneId];
      if (nextIndex !== undefined) {
        setCurrentSceneIndex(nextIndex);
      }
    } else if (currentSceneIndex < scenes.length - 1) {
      // Move to next scene
      setCurrentSceneIndex(currentSceneIndex + 1);
    } else if (loop) {
      // Start over if looping
      setCurrentSceneIndex(0);
    } else {
      // Stop playing if we've reached the end
      setIsPlaying(false);
    }
  };
  
  // Navigation handlers
  const goToNextScene = () => {
    if (currentSceneIndex < scenes.length - 1) {
      setCurrentSceneIndex(currentSceneIndex + 1);
    } else if (loop) {
      setCurrentSceneIndex(0);
    }
  };
  
  const goToPreviousScene = () => {
    if (currentSceneIndex > 0) {
      setCurrentSceneIndex(currentSceneIndex - 1);
    } else if (loop) {
      setCurrentSceneIndex(scenes.length - 1);
    }
  };
  
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
      {/* Header with title and controls */}
      <div className="relative z-20 p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gray-800">
        <div className="flex items-center">
          <div className="bg-blue-600/20 p-2 rounded-full mr-3">
            <Play className="text-blue-400" size={20} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">{title}</h2>
            {description && <p className="text-gray-400 mt-1 text-sm">{description}</p>}
          </div>
        </div>
        
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          <button
            onClick={goToPreviousScene}
            className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
            aria-label="Previous Scene"
          >
            <ChevronLeft size={20} />
          </button>
          
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="p-2 rounded-full bg-blue-600 hover:bg-blue-700 transition-colors"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
          </button>
          
          <button
            onClick={goToNextScene}
            className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
            aria-label="Next Scene"
          >
            <ChevronRight size={20} />
          </button>
          
          <button
            onClick={() => {
              setCurrentSceneIndex(0);
              setIsPlaying(true);
            }}
            className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
            aria-label="Reset"
          >
            <RotateCcw size={20} />
          </button>
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="relative h-1 bg-gray-800">
        <div
          className="absolute top-0 left-0 h-full bg-blue-600 transition-all duration-300"
          style={{ width: `${((currentSceneIndex + 1) / scenes.length) * 100}%` }}
        />
      </div>
      
      {/* Scene information */}
      <div className="bg-gray-900/50 p-6">
        <div className="flex items-center">
          <div className="bg-gray-800 p-3 rounded-full mr-3">
            {currentScene.icon}
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">{currentScene.title}</h3>
            <p className="text-gray-300">{currentScene.description}</p>
          </div>
        </div>
      </div>
      
      {/* Scene animation */}
      <div className="bg-gray-900 p-6 min-h-[300px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSceneIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="h-full"
          >
            {React.createElement(currentScene.animationComponent, {
              isActive: isPlaying,
              onComplete: handleSceneComplete
            })}
          </motion.div>
        </AnimatePresence>
      </div>
      
      {/* Navigation dots */}
      <div className="p-4 flex justify-center">
        <div className="flex space-x-2">
          {scenes.map((scene, idx) => (
            <button
              key={idx}
              className={`w-3 h-3 rounded-full ${
                idx === currentSceneIndex ? 'bg-blue-500' : 'bg-gray-700'
              }`}
              onClick={() => setCurrentSceneIndex(idx)}
              aria-label={`Go to scene ${idx + 1}: ${scene.title}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Update the WorkflowVisualizationProps interface
interface WorkflowVisualizationProps {
  title?: string;
  description?: string;
  scenes?: any[];
  workflowId?: string;
  sections?: any[];
  className?: string;
}

// Update the WorkflowVisualization component
const WorkflowVisualization = ({ 
  title, 
  description, 
  scenes = [], 
  workflowId, 
  sections = [],
  className = ""
}: WorkflowVisualizationProps) => {
  // If no title is provided, try to generate one from the sections
  const effectiveTitle = title || (sections && sections.length > 0 ? 'Workflow Process' : 'Workflow Visualization');
  
  // Convert sections to scenes if sections are provided
  const effectiveScenes = scenes.length > 0 ? scenes : sections.map((section: any, index: number) => {
    // Find a processOverviewCard component if available
    const processComponent = section.components?.find((c: any) => c.type === 'processOverviewCard');
    
    // Extract steps from the process overview component if found
    const steps = processComponent?.steps || [];
    
    return {
      id: section.id || `section-${index}`,
      title: section.title || `Step ${index + 1}`,
      description: section.description || '',
      icon: <Database size={24} />,
      animationComponent: DataScrapingAnimation, // Default animation
    };
  });
  
  return (
    <div className={`mt-8 mb-16 ${className}`}>
      <WorkflowStoryPlayer
        title={effectiveTitle}
        description={description}
        scenes={effectiveScenes.length > 0 ? effectiveScenes : [
          {
            id: 'data-collection',
            title: 'Data Collection',
            description: 'Collecting and processing data from various sources',
            icon: <Database size={24} />,
            animationComponent: DataScrapingAnimation,
            nextSceneId: 'analysis'
          },
          {
            id: 'analysis',
            title: 'Data Analysis',
            description: 'AI algorithms analyze and extract insights',
            icon: <Brain size={24} />,
            animationComponent: QueryDatabaseAnimation,
            nextSceneId: 'decision'
          },
          {
            id: 'decision',
            title: 'Decision Making',
            description: 'System makes or recommends decisions based on analysis',
            icon: <Bot size={24} />,
            animationComponent: OptimizationAnimation
          }
        ]}
        autoPlay={true}
        loop={true}
      />
    </div>
  );
};

export default WorkflowVisualization;